import { AssignmentFormData, assignmentFormDefaultValues } from '../types/assignmentTypes';
import {
  MatchingCriteria,
  Workflow,
  WorkflowReponseType,
  WorkflowType,
} from '@/client/services/api/graphql/gql/graphql';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import AdminAssignmentsService from '../api/AdminAssignmentsService';
import { UseFormReset } from 'react-hook-form';
import { useAdminAssignmentsStore } from '../state/adminAssignmentsStore';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useReadLocalStorage } from 'usehooks-ts';

// * This hook handles loading data for the assignment edit form for both automated assignments and direct assignment instances

export const useGetAssignmentFormEditData = (
  reset: UseFormReset<Omit<AssignmentFormData, 'percentComplete' | 'resultGroupCount'>>,
) => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const learnAppContentAssignmentSelection = useReadLocalStorage<any[]>(
    'learn-app-assignment-content-selection',
  );
  const {
    selectedInstanceCreationData,
    selectedWorkflowDuplicateData,
    setCurrentInstanceParentWorkflowId,
  } = useAdminAssignmentsStore();
  const { pathname } = location;
  const isInstance = pathname.includes('instance');
  const isCreate = pathname.includes('create');

  const defaultBulkAudienceImportData = {
    usesBulkAudienceImport: false,
    bulkImportFileName: '',
  };

  const { data, isLoading, isFetching, isError } = useQuery({
    // @ts-expect-error - id is not always defined
    enabled: !!id,
    queryKey: ['getAssignmentFormEditData', id, isInstance],
    queryFn: () =>
      isInstance
        ? AdminAssignmentsService.getWorkflowResultGroupDetails(id)
        : AdminAssignmentsService.getWorkflowDetails(id),
  });

  useEffect(() => {
    if (isInstance && isCreate) {
      if (!selectedInstanceCreationData) {
        navigate('/admin/assignments/overview');

        return;
      }

      reset({
        ...selectedInstanceCreationData,
        bulkAudienceImportData: { ...defaultBulkAudienceImportData },
        setDueDate:
          (selectedInstanceCreationData.settings.daysToComplete &&
            selectedInstanceCreationData.settings.daysToComplete > 0) ||
          false,
        simpleType:
          selectedInstanceCreationData.settings.type === WorkflowType.PointInTime
            ? 'pointInTime'
            : 'automated',
      });
    }

    if (isCreate && selectedWorkflowDuplicateData) {
      reset({
        ...selectedWorkflowDuplicateData,
        bulkAudienceImportData: { ...defaultBulkAudienceImportData },
        useCustomDueDate: selectedWorkflowDuplicateData.settings.type === WorkflowType.PointInTime,
        setDueDate:
          (selectedWorkflowDuplicateData.settings.daysToComplete &&
            selectedWorkflowDuplicateData.settings.daysToComplete > 0) ||
          false,
        id: '',
        createdAt: '',
        updatedAt: '',
        user: {
          id: '',
          fullName: '',
        },
        simpleType:
          selectedWorkflowDuplicateData.settings.type === WorkflowType.PointInTime
            ? 'pointInTime'
            : 'automated',
        addEscalationContacts: selectedWorkflowDuplicateData.settings.escalationContacts.length > 0,
        formBoundType: selectedWorkflowDuplicateData.settings.type,
        useAssignmentDelay: selectedWorkflowDuplicateData.settings?.assignmentOptions?.delay
          ? Number(selectedWorkflowDuplicateData.settings.assignmentOptions.delay) > 0
          : false,
        settings: {
          ...selectedWorkflowDuplicateData.settings,
          name: `[COPY] ${selectedWorkflowDuplicateData.settings.name}`,
        },
      });
    }
  }, []);

  useEffect(() => {
    if (data) {
      const { audience, workflow } = data;

      if (workflow) {
        setCurrentInstanceParentWorkflowId(workflow);
      }

      const matchAnySelections = audience.filter(
        (item) => item.matchingCriteria === MatchingCriteria.MatchAny,
      );
      const matchAllSelections = audience.filter(
        (item) => item.matchingCriteria === MatchingCriteria.MatchAll,
      );
      const matchNoneSelections = audience.filter(
        (item) => item.matchingCriteria === MatchingCriteria.MatchNone,
      );

      reset({
        ...data,
        bulkAudienceImportData: { ...defaultBulkAudienceImportData },
        useCustomDueDate: data.settings.type === WorkflowType.PointInTime,
        setDueDate: (data.settings.daysToComplete && data.settings.daysToComplete > 0) || false,
        simpleType: data.settings.type === WorkflowType.PointInTime ? 'pointInTime' : 'automated',
        addEscalationContacts: data.settings.escalationContacts.length > 0,
        formBoundType: data.settings.type,
        useAssignmentDelay: data.settings?.assignmentOptions?.delay
          ? Number(data.settings.assignmentOptions.delay) > 0
          : false,
        includeAudience: matchAnySelections.length > 0 || matchAllSelections.length > 0,
        excludeAudience: matchNoneSelections.length > 0,
      } as unknown as Workflow);
    }
  }, [data]);

  useEffect(() => {
    if (learnAppContentAssignmentSelection && !id) {
      reset({
        ...assignmentFormDefaultValues,
        settings: {
          ...assignmentFormDefaultValues.settings,
          responses: learnAppContentAssignmentSelection.map((item) => ({
            content: {
              contentType: item.type,
              id: item._id,
              isRecompletable: item.type === 'imported_content' ? 'false' : 'true',
              title: item.title || item.name,
            },
            type: WorkflowReponseType.Assignment,
          })),
        },
      });
    }
  }, [learnAppContentAssignmentSelection]);

  return { data, isLoading, isFetching, isError };
};
